import React, { useState, useEffect } from 'react';
import { dataService } from '../../services/dataService';
import { District } from '../../data/types';
import { toast } from 'react-toastify';
import PageContainer from '../layouts/PageContainer';
import ContentContainer from '../layouts/ContentContainer';
import { format } from 'date-fns';

const SuperAdminDistrictManagementScreen: React.FC = () => {
  const [districts, setDistricts] = useState<District[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState<District | null>(null);
  const [formDistrict, setFormDistrict] = useState({
    id: '',
    name: '',
    address: '',
    phone: '',
    website: '',
    logo: '' as string | undefined,
    totalSchools: 0,
    totalStudents: 0,
    totalTeachers: 0,
    status: 'active' as 'active' | 'inactive',
    lastSynced: '',
    createdAt: '',
    updatedAt: ''
  });



  const fetchDistricts = async () => {
    try {
      setLoading(true);
      const districtsData = await dataService.getDistricts();
      setDistricts(districtsData);
    } catch (error) {
      console.error('Error fetching districts:', error);
      toast.error('Failed to load districts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistricts();
  }, []);

  // Format date to MM/DD/YYYY
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MM/dd/yyyy');
    } catch (error) {
      return dateString;
    }
  };

  // Format number with commas
  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const handleAddDistrict = async () => {
    try {
      const newDistrictData = {
        name: formDistrict.name,
        address: formDistrict.address,
        phone: formDistrict.phone,
        website: formDistrict.website,
        logo: formDistrict.logo || '/images/districts/default-logo.png',
        totalSchools: 0,
        totalStudents: 0,
        totalTeachers: 0,
        status: formDistrict.status,
        lastSynced: new Date().toISOString()
      };

      await dataService.createDistrict(newDistrictData);
      await fetchDistricts();
      setShowAddModal(false);
      resetForm();
      toast.success('District added successfully');
    } catch (error) {
      console.error('Error adding district:', error);
      toast.error('Failed to add district');
    }
  };

  const handleEditDistrict = async () => {
    if (!selectedDistrict) return;
    
    try {
      await dataService.updateDistrict(formDistrict as District);
      await fetchDistricts();
      setShowEditModal(false);
      setSelectedDistrict(null);
      resetForm();
      toast.success('District updated successfully');
    } catch (error) {
      console.error('Error updating district:', error);
      toast.error('Failed to update district');
    }
  };

  const handleDeleteDistrict = async () => {
    if (!selectedDistrict) return;
    
    try {
      await dataService.deleteDistrict(selectedDistrict.id);
      await fetchDistricts();
      setShowDeleteConfirm(false);
      setSelectedDistrict(null);
      toast.success('District deleted successfully');
    } catch (error) {
      console.error('Error deleting district:', error);
      toast.error('Failed to delete district');
    }
  };

  const openEditModal = (district: District) => {
    setSelectedDistrict(district);
    // Create a copy of the district with all required properties
    setFormDistrict({
      ...district,
      logo: district.logo || ''
    });
    setShowEditModal(true);
  };

  const openDeleteConfirm = (district: District) => {
    setSelectedDistrict(district);
    setShowDeleteConfirm(true);
  };

  const resetForm = () => {
    setFormDistrict({
      id: '',
      name: '',
      address: '',
      phone: '',
      website: '',
      logo: '',
      totalSchools: 0,
      totalStudents: 0,
      totalTeachers: 0,
      status: 'active',
      lastSynced: '',
      createdAt: '',
      updatedAt: ''
    });
  };

  const handleAddModalOpen = () => {
    setShowAddModal(true);
    resetForm();
  };

  const addButton = (
    <button
      className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded inline-flex items-center"
      onClick={handleAddModalOpen}
    >
      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
      Add District
    </button>
  );

  return (
    <PageContainer title="District Management" actionButton={addButton} userRole="SuperAdmin">
      <ContentContainer>
        {/* Add District Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg">
              <h2 className="text-xl font-bold mb-4">Add New District</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">District Name</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={formDistrict.name}
                    onChange={(e) => setFormDistrict({...formDistrict, name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={formDistrict.address}
                    onChange={(e) => setFormDistrict({...formDistrict, address: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={formDistrict.phone}
                    onChange={(e) => setFormDistrict({...formDistrict, phone: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={formDistrict.website}
                    onChange={(e) => setFormDistrict({...formDistrict, website: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <button 
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="px-4 py-2 bg-primary text-white rounded-md"
                  onClick={handleAddDistrict}
                >
                  Add District
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit District Modal */}
        {showEditModal && selectedDistrict && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg">
              <h2 className="text-xl font-bold mb-4">Edit District</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">District Name</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={formDistrict.name}
                    onChange={(e) => setFormDistrict({...formDistrict, name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={formDistrict.address}
                    onChange={(e) => setFormDistrict({...formDistrict, address: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={formDistrict.phone}
                    onChange={(e) => setFormDistrict({...formDistrict, phone: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={formDistrict.website}
                    onChange={(e) => setFormDistrict({...formDistrict, website: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <button 
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="px-4 py-2 bg-primary text-white rounded-md"
                  onClick={handleEditDistrict}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && selectedDistrict && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">Confirm Delete</h2>
              <p className="mb-6">Are you sure you want to delete the district "{selectedDistrict.name}"? This action cannot be undone.</p>
              <div className="flex justify-end space-x-2">
                <button 
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button 
                  className="px-4 py-2 bg-red-600 text-white rounded-md"
                  onClick={handleDeleteDistrict}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Districts List */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="overflow-x-auto relative shadow-md sm:rounded-lg">
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th scope="col" className="py-3 px-6">District Name</th>
                  <th scope="col" className="py-3 px-6">Location</th>
                  <th scope="col" className="py-3 px-6">Schools</th>
                  <th scope="col" className="py-3 px-6">Students</th>
                  <th scope="col" className="py-3 px-6">Teachers</th>
                  <th scope="col" className="py-3 px-6">Status</th>
                  <th scope="col" className="py-3 px-6">Created</th>
                  <th scope="col" className="py-3 px-6">Actions</th>
                </tr>
              </thead>
              <tbody>
                {districts.map((district) => (
                  <tr key={district.id} className="bg-white border-b hover:bg-gray-50">
                    <th scope="row" className="py-4 px-6 font-medium text-gray-900 whitespace-nowrap">
                      <button 
                        onClick={() => openEditModal(district)}
                        className="font-medium text-primary hover:underline"
                      >
                        {district.name}
                      </button>
                    </th>
                    <td className="py-4 px-6">
                      {district.address}
                    </td>
                    <td className="py-4 px-6">{formatNumber(district.totalSchools)}</td>
                    <td className="py-4 px-6">{formatNumber(district.totalStudents)}</td>
                    <td className="py-4 px-6">{formatNumber(district.totalTeachers)}</td>
                    <td className="py-4 px-6">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        district.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {district.status}
                      </span>
                    </td>
                    <td className="py-4 px-6">{formatDate(district.createdAt)}</td>
                    <td className="py-4 px-6 flex space-x-2">
                      <button 
                        onClick={() => openEditModal(district)}
                        className="font-medium text-primary hover:underline"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => openDeleteConfirm(district)}
                        className="font-medium text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default SuperAdminDistrictManagementScreen;
